/**
 * Privacy Policy — globally acceptable.
 * Covers: GDPR (EU), CCPA/CPRA (California), DPDP Act 2023 (India),
 * UK DPA, PIPEDA (Canada). Tailored for a health & fitness app.
 */
import { LegalLayout, Section, List } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      effectiveDate="5 February 2026"
      testid="privacy-policy-page"
    >
      <p>
        This Privacy Policy explains how FitFrik (operated by FitFrik Technologies,
        &ldquo;FitFrik&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;)
        collects, uses, shares, and protects your personal information when you use our
        mobile and web applications, website, and related services (collectively, the
        &ldquo;Services&rdquo;).
      </p>
      <p>
        We design FitFrik to minimise the data we collect and to give you meaningful
        control. If you do not agree with any part of this Policy, please do not use the
        Services.
      </p>

      <Section n="1" title="Who we are & how to contact us">
        <p>
          FitFrik is operated by FitFrik Technologies. You can reach our Data Protection
          Officer at{" "}
          <a className="underline" href="mailto:support@fitfrik.com">
            support@fitfrik.com
          </a>
          . If you are in the European Economic Area, the United Kingdom, or India you
          also have the right to lodge a complaint with your local supervisory authority.
        </p>
      </Section>

      <Section n="2" title="Information we collect">
        <p>We collect only what we need to run the Services. Specifically:</p>
        <List
          items={[
            "Account information: email, name, password hash (bcrypt), profile photo, and authentication provider (email or Google).",
            "Profile and onboarding data: age, sex, height, weight, goal, dietary preference, preferred language, and city.",
            "Health & fitness data: workout sessions, exercises performed, sets/reps, calories, heart-rate (if provided by wearables), steps, sleep, habit streaks, weight check-ins, and body composition estimates from photo scans.",
            "Nutrition data: meals logged, food photographs you upload, the AI-derived macro/micro breakdown, and barcode lookups.",
            "Messages and content: AI nutritionist chats, community posts, enquiries sent to trainers/nutritionists, and posts you like, comment on, or report.",
            "Payments: Razorpay order and payment identifiers, subscription tier, invoice history. We never see or store your card number, UPI PIN, or bank credentials.",
            "Device and usage data: IP address, device model, OS, app version, language, timezone, crash reports, and feature usage events.",
            "Location: approximate city based on IP (for trainer recommendations). We do not track precise GPS unless you explicitly grant that permission.",
          ]}
        />
        <p>
          Some data (for example food photos and body scans) is <em>sensitive personal
          data</em> in most jurisdictions. We treat it with enhanced safeguards — see
          Section 7.
        </p>
      </Section>

      <Section n="3" title="How we use your information">
        <List
          items={[
            "To provide and personalise core features (workout generation, food scanning, AI nutritionist chat, health dashboards).",
            "To authenticate you and keep your account secure, including bcrypt password hashing, JWT session tokens, rate limiting, and anomaly detection.",
            "To process subscription payments via Razorpay and to issue invoices required by Indian tax law.",
            "To power AI features: your food photos, body scan photos, and chat prompts are sent to our AI processors (see Section 5) solely to generate a response and are not used to train their public models.",
            "To communicate with you about reminders, streaks, broadcasts, and account notices. You can adjust these in Settings → Notifications.",
            "To analyse aggregated usage so we can improve the product — this analysis uses pseudonymised data.",
            "To comply with legal obligations, enforce our Terms, prevent fraud, and protect the safety of our users.",
          ]}
        />
      </Section>

      <Section n="4" title="Legal bases for processing (EEA / UK)">
        <p>
          Depending on the purpose, we rely on the following legal bases under the GDPR
          and UK GDPR:
        </p>
        <List
          items={[
            "Contract: to provide the Services you signed up for.",
            "Legitimate interest: to secure our Services, prevent abuse, and improve features.",
            "Consent: for processing sensitive health data, sending optional marketing, and accessing device sensors. You can withdraw consent at any time from Settings.",
            "Legal obligation: to comply with tax, anti-money-laundering, and data protection laws.",
          ]}
        />
      </Section>

      <Section n="5" title="Service providers and sub-processors">
        <p>
          We share limited data with carefully selected service providers that help us
          operate the Services. Each is bound by contractual confidentiality and data-
          protection obligations.
        </p>
        <List
          items={[
            "Google LLC — optional sign-in via Google OAuth.",
            "OpenAI, Anthropic, and Google AI (Gemini) — LLM inference for workouts, chat, food analysis, and body composition insights. Your prompts are retained by the providers only for abuse monitoring per their enterprise terms.",
            "ElevenLabs — text-to-speech audio coach (only if you opt in).",
            "Razorpay — payment processing for subscriptions and trainer bookings.",
            "Amazon Web Services — video and static asset delivery via S3 + CloudFront.",
            "Open Food Facts — open-data barcode lookups for packaged foods.",
            "MongoDB Atlas — encrypted database hosting (India region).",
            "Cloudflare — web firewall and CDN.",
          ]}
        />
        <p>
          We do not sell your personal information and we do not use it to train third-
          party AI models.
        </p>
      </Section>

      <Section n="6" title="International data transfers">
        <p>
          FitFrik is operated from India. If you are outside India, your data will be
          transferred to and processed in India (and potentially in the United States,
          where our AI and storage providers operate). Transfers from the EEA/UK rely on
          Standard Contractual Clauses. Transfers into India comply with the Digital
          Personal Data Protection Act, 2023.
        </p>
      </Section>

      <Section n="7" title="Security">
        <List
          items={[
            "All traffic is encrypted in transit via TLS 1.2+.",
            "Passwords are hashed with bcrypt (cost factor 12). We never store plain-text passwords.",
            "Food photos, body scan photos, and AI chat transcripts are encrypted at rest.",
            "Access to production systems is limited to authorised engineers, logged, and reviewed.",
            "We run automated vulnerability scans, keep dependencies patched, and perform periodic penetration tests.",
            "If we become aware of a personal data breach likely to pose a risk to you, we will notify you and the relevant supervisory authorities within 72 hours as required by law.",
          ]}
        />
      </Section>

      <Section n="8" title="Data retention">
        <p>
          We keep your data only as long as necessary to provide the Services and to meet
          legal obligations:
        </p>
        <List
          items={[
            "Account data: retained while your account is active; soft-deleted for 30 days upon request; then purged.",
            "Food scans and body scans: stored while your account is active. You can delete individual entries at any time from the app.",
            "AI chat transcripts: retained for 90 days for abuse prevention, then deleted unless you star / save a conversation.",
            "Payment records: retained for 8 financial years as required by Indian tax law.",
            "Rate-limit and security logs: 12 months.",
          ]}
        />
      </Section>

      <Section n="9" title="Your rights">
        <p>
          Subject to your jurisdiction, you have the right to access, correct, port,
          restrict, or delete your personal information, and to object to certain kinds
          of processing. Specifically:
        </p>
        <List
          items={[
            "Access: download your data from Settings → Privacy → Export my data.",
            "Correction: edit your profile in Settings → Edit Profile.",
            "Deletion: request account deletion from Settings → Delete Account. We soft-delete for 30 days and then hard-purge.",
            "Portability: your workout + nutrition data can be exported as CSV from the Health dashboard.",
            "Withdraw consent: toggle notifications, device access, or AI features in Settings.",
            "Complain: to your local supervisory authority. For India, the Data Protection Board established under the DPDP Act, 2023.",
          ]}
        />
        <p>
          To exercise any right, email{" "}
          <a className="underline" href="mailto:support@fitfrik.com">
            support@fitfrik.com
          </a>
          . We respond within 30 days (or shorter where required by law).
        </p>
      </Section>

      <Section n="10" title="Children">
        <p>
          FitFrik is not intended for users under 16. We do not knowingly collect data
          from children under 16. If you believe a child has provided us with personal
          information, please contact us and we will delete it promptly.
        </p>
      </Section>

      <Section n="11" title="Cookies and similar technologies">
        <p>
          We use a minimal set of first-party cookies for authentication
          (<code>session_token</code>), session state, and analytics. We do not use
          third-party advertising or cross-site tracking cookies. You can clear or block
          cookies in your browser settings; doing so may break login.
        </p>
      </Section>

      <Section n="12" title="California privacy (CCPA / CPRA)">
        <p>
          Californian residents have additional rights including the right to know, the
          right to delete, the right to correct, and the right to opt out of &ldquo;sale
          or sharing&rdquo; of personal information. FitFrik does not sell or share your
          personal information as those terms are defined under the CPRA.
        </p>
      </Section>

      <Section n="13" title="Changes to this Policy">
        <p>
          We may update this Policy from time to time. If changes are material, we will
          notify you in the app or by email at least 14 days before they take effect. The
          &ldquo;Effective&rdquo; date at the top reflects the last revision.
        </p>
      </Section>
    </LegalLayout>
  );
}
